/**
 * Error reports to Sentry for the Command Centre (Product, 2 Oct 2026, board item
 * error-alerts-silent). Before this, an error here reached nobody.
 *
 * Off unless VITE_SENTRY_DSN is set at build time. Errors only: no
 * performance tracing, no session replay, and no user tag. Every event
 * passes the shared scrubber (telemetryScrub, same as the learner site),
 * then `tighten` below, before it leaves the browser.
 */
import { scrubEvent } from './telemetryScrub';

type Sentry = typeof import('@sentry/react');
type Crumb = { category?: string; message?: string; data?: Record<string, unknown> };

let sentry: Sentry | null = null;
let started = false;

/**
 * This app shows learners' and facilitators' names on screen, and a name
 * is plain text no pattern can spot. A click or a console line can carry
 * one, so those breadcrumbs keep only their kind, level and time.
 */
export function tighten<C extends Crumb>(crumb: C): C {
  if (crumb.category === 'console' || crumb.category?.startsWith('ui.')) {
    const rest = { ...crumb };
    delete rest.message;
    delete rest.data;
    return rest;
  }
  return crumb;
}

/**
 * Short prefixed ids (ws_15800f17d7e3 for a drill room, sec_1a2b3c4d and
 * a dozen more): the shared filter only catches UUIDs and 32+ character
 * strings (Web, 2 Oct). Any "letters_hex" word with a digit in it becomes
 * ":id", in every string except code locations and Sentry's own ids.
 */
// One-way codes (h_ + 16 hex) are kept, as on the learner site, so the rule
// stays the same in all three apps.
const SHORT_ID = /\b(?!h_[0-9a-f]{16}\b)[a-z]{1,10}_(?=[0-9a-f]*\d)[0-9a-f]{6,}\b/g;
const KEEP_KEY = /^(filename|function|module|abs_path|lineno|colno|in_app|event_id|trace_id|span_id|parent_span_id|sid)$/;

function dropShortIds(v: unknown, key: string | null, depth: number): unknown {
  if (key !== null && KEEP_KEY.test(key)) return v;
  if (typeof v === 'string') return v.replace(SHORT_ID, ':id');
  if (v === null || typeof v !== 'object' || depth > 10) return v;
  if (Array.isArray(v)) return v.map((x) => dropShortIds(x, null, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) out[k] = dropShortIds(x, k, depth + 1);
  return out;
}

/** Scrub, then drop short ids, then tighten the breadcrumbs. Exported for the test. */
export function cleanEvent<E extends object>(event: E): E {
  const out = dropShortIds(scrubEvent(event), null, 0) as E & { breadcrumbs?: Crumb[] };
  if (Array.isArray(out.breadcrumbs)) out.breadcrumbs = out.breadcrumbs.map(tighten);
  return out;
}

/** Start Sentry if a DSN was built in. Call once at boot. */
export async function initTelemetry(): Promise<void> {
  if (started) return;
  started = true;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  // Read now, before the first await: a sign-in redirect (Command Centre,
  // 3 Oct) replaces the address and drops ?sentry-test before Sentry loads.
  const wantTest = new URLSearchParams(window.location.search).has('sentry-test');
  try {
    const S = await import('@sentry/react');
    S.init({
      dsn,
      environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined)
        || (window.location.hostname.includes('preview') ? 'preview' : import.meta.env.MODE),
      release: (import.meta.env.VITE_BUILD_SHA as string | undefined) || undefined,
      initialScope: { tags: { app: 'command-centre' } },
      sendDefaultPii: false,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      beforeSend: (event) => cleanEvent(event),
      beforeBreadcrumb: (crumb) => {
        const c = cleanEvent({ breadcrumbs: [crumb] }).breadcrumbs?.[0];
        return c ? tighten(c) : null;
      },
    });
    sentry = S;
    // For Reliability's check: open the app with ?sentry-test in the address
    // and one harmless test message is sent.
    if (wantTest) {
      S.captureMessage('Sentry test from the Command Centre', 'info');
    }
  } catch {
    // Reporting must never break the app.
  }
}

/** Report a caught error. Does nothing when Sentry is off. */
export function captureException(err: unknown): void {
  sentry?.captureException(err);
}
